// Canonical Cabinet Bay + Fireplace Face preview components.
// Rendered directly from `cabinet_config` / `fireplace_config` — no
// separate representation, no invented rates. The main 2D schematic
// cabinet rendering AND the Properties live preview both consume this
// module so what the studio sees in Properties is exactly what the
// canvas draws. Downstream 3D + Construction Docs will also derive
// from the same canonical config.

import React from "react";

// Compute the actual bay widths in mm. If the cabinet_config layout
// is `even_split`, widths are recomputed to equal fractions of the
// cabinet width every render. Otherwise stored per-bay widths are
// used — if they sum to a value different from the cabinet width,
// they are proportionally normalised so the visual and 2D always fit
// the cabinet perfectly (canonical width remains the source of truth).
export function computeBayWidths(cabinetConfig, cabinetWidthMm) {
  const bays = (cabinetConfig && cabinetConfig.bays) || [];
  if (bays.length === 0) return [];
  if (cabinetConfig.layout_mode === "even_split") {
    const each = cabinetWidthMm / bays.length;
    return bays.map(() => each);
  }
  const total = bays.reduce((s, b) => s + (Number(b.width_mm) || 0), 0);
  if (total <= 0) {
    const each = cabinetWidthMm / bays.length;
    return bays.map(() => each);
  }
  const scale = cabinetWidthMm / total;
  return bays.map((b) => (Number(b.width_mm) || 0) * scale);
}

// ── Canonical single-bay renderer (SVG) ─────────────────────────────
function renderBay(bay, x, y, w, h) {
  const kind = bay.kind || "door";
  const stroke = "currentColor";
  if (kind === "door") {
    // ── Canonical elevation handing triangle ────────────────────
    // Two diagonals originate from the two corners of the HINGE
    // side and converge at the midpoint of the opposite HANDLE
    // side. Derived entirely from `bay.hinge` — never store the
    // line coordinates. Handle mark is a short vertical stroke on
    // the HANDLE side. Pair doors render as two mirrored leaves.
    const hinge = bay.hinge || "right";
    const midY = y + h / 2;
    const drawSingle = (bx, bw, side) => {
      // side = "left"  → diagonals from top-left+bottom-left,
      //                  meet at right midpoint (handle on right).
      // side = "right" → diagonals from top-right+bottom-right,
      //                  meet at left midpoint  (handle on left).
      if (side === "left") {
        return (
          <>
            <line x1={bx}      y1={y}        x2={bx + bw} y2={midY}  stroke={stroke} strokeWidth="1"/>
            <line x1={bx}      y1={y + h}    x2={bx + bw} y2={midY}  stroke={stroke} strokeWidth="1"/>
            <line x1={bx + bw - 3} y1={midY - 3} x2={bx + bw - 3} y2={midY + 3} stroke={stroke} strokeWidth="1"/>
          </>
        );
      }
      return (
        <>
          <line x1={bx + bw} y1={y}     x2={bx} y2={midY} stroke={stroke} strokeWidth="1"/>
          <line x1={bx + bw} y1={y + h} x2={bx} y2={midY} stroke={stroke} strokeWidth="1"/>
          <line x1={bx + 3} y1={midY - 3} x2={bx + 3} y2={midY + 3} stroke={stroke} strokeWidth="1"/>
        </>
      );
    };
    return (
      <g key={bay.id || `${x}`}>
        <rect x={x + 0.5} y={y + 0.5} width={Math.max(0, w - 1)} height={Math.max(0, h - 1)}
              fill="none" stroke={stroke} strokeWidth="1"/>
        {hinge === "left"  && drawSingle(x, w, "left")}
        {hinge === "right" && drawSingle(x, w, "right")}
        {hinge === "pair"  && (
          <>
            {/* Physical centre divider between the two leaves. */}
            <line x1={x + w / 2} y1={y} x2={x + w / 2} y2={y + h} stroke={stroke} strokeWidth="0.75"/>
            {/* Left leaf hinged at far LEFT, handle on centre. */}
            {drawSingle(x, w / 2, "left")}
            {/* Right leaf hinged at far RIGHT, handle on centre. */}
            {drawSingle(x + w / 2, w / 2, "right")}
          </>
        )}
        {/* hinge === "none" → no notation, plain rectangle only. */}
      </g>
    );
  }
  if (kind === "drawer") {
    // Canonical drawer records for `custom` layout — [{id, height_mm}].
    // Fall back to legacy `drawer_heights_mm` array, then to even split
    // driven by `drawer_count`. Every height is treated as mm; the
    // renderer normalises to available px so proportional accuracy is
    // preserved even when the sum is slightly off (validation warns
    // in the Properties editor — the renderer never silently discards
    // data).
    let heights;
    if (bay.drawer_layout === "custom" && Array.isArray(bay.drawers) && bay.drawers.length > 0) {
      heights = bay.drawers.map((d) => Number((d && d.height_mm) || 0));
    } else if (Array.isArray(bay.drawer_heights_mm) && bay.drawer_heights_mm.length > 0) {
      heights = bay.drawer_heights_mm.map((v) => Number(v) || 0);
    } else {
      const n = Math.max(1, Number(bay.drawer_count) || 1);
      heights = Array.from({ length: n }, () => h / n);
    }
    const total = heights.reduce((s, v) => s + (Number(v) || 0), 0) || h;
    const scale = h / total;
    let cy = y;
    return (
      <g key={bay.id || `${x}`}>
        <rect x={x + 0.5} y={y + 0.5} width={Math.max(0, w - 1)} height={Math.max(0, h - 1)}
              fill="none" stroke={stroke} strokeWidth="1"/>
        {heights.map((mm, i) => {
          const dh = (Number(mm) || 0) * scale;
          const rowY = cy;
          cy += dh;
          return (
            <g key={i}>
              {i > 0 && (
                <line x1={x} y1={rowY} x2={x + w} y2={rowY}
                      stroke={stroke} strokeWidth="1"/>
              )}
              <line x1={x + w / 2 - 3} y1={rowY + dh / 2}
                    x2={x + w / 2 + 3} y2={rowY + dh / 2}
                    stroke={stroke} strokeWidth="1"/>
            </g>
          );
        })}
      </g>
    );
  }
  if (kind === "open") {
    // Open bay: outline + shelves. In `custom` layout each shelf
    // renders at its exact canonical y_mm (measured from the interior
    // bottom, Y-up). Shelves are rendered as thin horizontal strokes —
    // canonical shelf thickness is not yet modelled, per V4 Part I5.
    const cabH = Number(bay.__cabinet_height_mm__) || 0;
    const shelvesCustom = bay.shelf_layout === "custom"
      && Array.isArray(bay.shelves_config)
      && bay.shelves_config.length > 0;
    return (
      <g key={bay.id || `${x}`}>
        <rect x={x + 0.5} y={y + 0.5} width={Math.max(0, w - 1)} height={Math.max(0, h - 1)}
              fill="none" stroke={stroke} strokeWidth="1" strokeDasharray="2 2"/>
        {shelvesCustom
          ? (bay.shelves_config || [])
              .filter((s) => s && Number(s.y_mm) > 0 && (cabH === 0 || Number(s.y_mm) < cabH))
              .map((s, i) => {
                // y_mm measured from bay interior bottom (Y-up). SVG y
                // grows DOWN, so invert against the bay's height. Use
                // the bay height in mm (bay.__height_mm__ if provided)
                // otherwise the cabinet-height fallback.
                const bayHmm = Number(bay.__height_mm__) || cabH || 1;
                const sy = y + h - (Number(s.y_mm) / bayHmm) * h;
                return (
                  <line key={s.id || i}
                        x1={x + 2} y1={sy} x2={x + w - 2} y2={sy}
                        stroke={stroke} strokeWidth="0.75"/>
                );
              })
          : (() => {
              const shelves = Math.max(0, Number(bay.shelves) || 0);
              const step = h / (shelves + 1);
              return Array.from({ length: shelves }, (_, i) => {
                const sy = y + step * (i + 1);
                return (
                  <line key={i} x1={x + 2} y1={sy} x2={x + w - 2} y2={sy}
                        stroke={stroke} strokeWidth="0.75"/>
                );
              });
            })()
        }
      </g>
    );
  }
  return null;
}

// Compact canonical preview. Renders responsively — the SVG grows to
// fill its container's available width while preserving canonical bay
// proportions. Height stays at the requested px. Consumers no longer
// need to know the container's live width.
//
// Bay numbering (PART 10) — display left-to-right position labels as
// a small, restrained header row so studio users can immediately
// correlate the diagram with the Bay configuration controls. Derived
// UI metadata only — NEVER stored on the canonical bay. Reordering,
// adding and removing bays instantly re-derives the display numbers.
// Numbers sit inside a reserved header strip above the bay bodies so
// door-handing triangles, drawer division lines and shelf lines all
// remain fully visible.
export function CabinetBayPreview({ cabinetConfig, cabinetWidthMm, cabinetHeightMm,
                                    pxWidth = 200, pxHeight = 90,
                                    showBayNumbers = true,
                                    onBayFocus = null,
                                    leftEndProfile = null,
                                    rightEndProfile = null,
                                    testid = "cab-bay-preview" }) {
  // Allow the silhouette to render even when no bays are configured
  // yet, so the designer can validate curved end profiles independently
  // of bay layout. When neither bays NOR end profiles exist, show the
  // "no bays configured" placeholder as before.
  const hasBays = !!(cabinetConfig && (cabinetConfig.bays || []).length);
  const hasEndProfile = !!(leftEndProfile || rightEndProfile);
  if (!hasBays && !hasEndProfile) {
    return (
      <div className="text-[10px] text-muted-foreground italic border border-dashed border-border p-2"
           data-testid={testid}>
        No bays configured
      </div>
    );
  }
  const widths = hasBays ? computeBayWidths(cabinetConfig, cabinetWidthMm || 1) : [];
  const scaleX = pxWidth / (cabinetWidthMm || 1);
  const bays = hasBays ? (cabinetConfig.bays || []) : [];
  const headerH = showBayNumbers ? 14 : 0;
  const bodyY   = headerH;
  const bodyH   = Math.max(1, pxHeight - headerH);
  const totalH  = pxHeight;
  // ── End-profile silhouette overlay (Phase 2B-0) ────────────
  // Draws the actual canonical curved end silhouette in 2D so the
  // designer sees the same geometry that will extrude into 3D. The
  // per-bay rectangles inside stay canonical; the outline reflects
  // the physical component silhouette.
  const endOffset = (p) => {
    if (!p || (p.kind || "square") === "square") return 0;
    if (p.kind === "radius")     return Number(p.radius_mm) || 0;
    if (p.kind === "semicircle") return (cabinetHeightMm || 0) / 2;
    if (p.kind === "bevel")      return Number(p.bevel_depth_mm) || 0;
    return 0;
  };
  const lOff = endOffset(leftEndProfile);
  const rOff = endOffset(rightEndProfile);
  const buildSilhouette = () => {
    if (!leftEndProfile && !rightEndProfile) return null;
    const y0 = bodyY;
    const y1 = bodyY + bodyH;
    const midY = bodyY + bodyH / 2;
    const w = pxWidth;
    const lPx = lOff * scaleX;
    const rPx = rOff * scaleX;
    const endArc = (side, profile, xTop, xBot) => {
      if (!profile || profile.kind === "square") {
        const x = side === "left" ? 0 : w;
        return `L ${x} ${y0} L ${x} ${y1}`;
      }
      if (profile.kind === "radius") {
        // R: quarter arc from top corner into midY-flat then to bottom.
        if (side === "left") {
          return `L ${lPx} ${y0} A ${lPx} ${bodyH / 2} 0 0 0 0 ${midY} A ${lPx} ${bodyH / 2} 0 0 0 ${lPx} ${y1}`;
        }
        return `L ${w - rPx} ${y0} A ${rPx} ${bodyH / 2} 0 0 1 ${w} ${midY} A ${rPx} ${bodyH / 2} 0 0 1 ${w - rPx} ${y1}`;
      }
      if (profile.kind === "semicircle") {
        // Full half-ellipse from top corner arc'd around to bottom.
        if (side === "left") {
          return `A ${lPx} ${bodyH / 2} 0 0 0 0 ${y1}`;
        }
        return `A ${rPx} ${bodyH / 2} 0 0 1 ${w} ${y1}`;
      }
      if (profile.kind === "bevel") {
        // 45°-style chamfer using canonical depth.
        if (side === "left") {
          return `L ${lPx} ${y0} L 0 ${midY} L ${lPx} ${y1}`;
        }
        return `L ${w - rPx} ${y0} L ${w} ${midY} L ${w - rPx} ${y1}`;
      }
      return "";
    };
    const startX = leftEndProfile && leftEndProfile.kind === "semicircle" ? lPx : 0;
    const endX   = rightEndProfile && rightEndProfile.kind === "semicircle" ? w - rPx : w;
    // Compose the closed path: start top-left, curve down left, across
    // bottom, curve up right, back to start.
    const topLeft   = leftEndProfile && leftEndProfile.kind === "semicircle"
      ? `M ${startX} ${y0}` : `M ${startX} ${y0}`;
    const leftDown  = leftEndProfile
      ? endArc("left", leftEndProfile, startX, startX)
      : `L 0 ${y0} L 0 ${y1}`;
    const bottomLine = `L ${endX} ${y1}`;
    const rightUp   = rightEndProfile
      ? endArc("right", rightEndProfile, endX, endX).replace(/^L [^\s]+ [^\s]+ /, "")
      : `L ${w} ${y1} L ${w} ${y0}`;
    const topLine   = `L ${startX} ${y0} Z`;
    const d = `${topLeft} ${leftDown} ${bottomLine} ${rightUp} ${topLine}`;
    return (
      <path d={d}
            data-testid="cabinet-silhouette"
            fill="none" stroke="currentColor" strokeWidth="1" strokeOpacity="0.9"/>
    );
  };
  let cx = 0;
  return (
    <svg viewBox={`0 0 ${pxWidth} ${totalH}`}
         data-testid={testid}
         className="block text-foreground/80 w-full h-auto max-w-full"
         style={{ maxHeight: totalH * 1.5 }}
         preserveAspectRatio="xMidYMid meet">
      {/* Outer cabinet outline — square/rectangular default. Curved
          silhouette (if present) is painted on top. */}
      {!leftEndProfile && !rightEndProfile && (
        <rect x={0.5} y={bodyY + 0.5} width={pxWidth - 1} height={bodyH - 1}
              fill="none" stroke="currentColor" strokeWidth="1"/>
      )}
      {buildSilhouette()}
      {bays.map((b, i) => {
        const wPx = widths[i] * scaleX;
        const bayWithCtx = { ...b, __cabinet_height_mm__: cabinetHeightMm || 0,
                             __height_mm__: cabinetHeightMm || 0 };
        const el = renderBay(bayWithCtx, cx, bodyY, wPx, bodyH);
        const numX = cx + wPx / 2;
        const numEl = showBayNumbers && (
          <g key={`num-${b.id || i}`}
             data-testid={`cab-bay-preview-number-${i}`}
             data-bay-id={b.id || ""}
             style={onBayFocus ? { cursor: "pointer" } : undefined}
             onClick={onBayFocus ? (ev) => { ev.stopPropagation(); onBayFocus(i, b); } : undefined}>
            <rect x={numX - 6} y={1} width={12} height={11} rx={2}
                  fill="hsl(var(--background))"
                  stroke="currentColor" strokeOpacity="0.35" strokeWidth="0.5"/>
            <text x={numX} y={9.5} fontSize="8"
                  textAnchor="middle" fontWeight="600"
                  fill="hsl(var(--foreground))"
                  aria-label={`Bay ${i + 1}`}>{i + 1}</text>
          </g>
        );
        cx += wPx;
        return (
          <g key={b.id || `bay-${i}`}>
            {el}
            {numEl}
          </g>
        );
      })}
    </svg>
  );
}

// ── Fireplace face diagram (top-down) ───────────────────────────────
// Renders a 4-face square with active faces highlighted so the studio
// can see the actual physical visibility of the selected sided-type.
const FACE_ORDER = ["front", "right", "rear", "left"];

export function FireplaceFacePreview({ fireplaceConfig, size = 90,
                                       testid = "fp-face-preview" }) {
  const active = new Set((fireplaceConfig && fireplaceConfig.visible_faces) || []);
  const s = size;
  const inset = 12;
  const inner = s - inset * 2;
  const stroke = "currentColor";
  const on = "hsl(var(--foreground))";
  const off = "hsl(var(--border))";
  const face = (key, x1, y1, x2, y2) => {
    const isOn = active.has(key);
    return (
      <line key={key} x1={x1} y1={y1} x2={x2} y2={y2}
            stroke={isOn ? on : off}
            strokeWidth={isOn ? 3 : 1}
            strokeLinecap="round"
            data-testid={`fp-face-preview-${key}`}
            data-active={isOn ? "true" : "false"}/>
    );
  };
  return (
    <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}
         data-testid={testid}
         className="block text-muted-foreground">
      {/* Firebox rectangle */}
      <rect x={inset} y={inset} width={inner} height={inner}
            fill="none" stroke={stroke} strokeWidth="0.5" strokeDasharray="2 2"/>
      {/* Room-forward direction indicator (down = viewer / room) */}
      <text x={s / 2} y={s - 2} fontSize="8" textAnchor="middle"
            fill="hsl(var(--muted-foreground))" opacity="0.6">room</text>
      {/* Faces: front=bottom, rear=top, left=left, right=right */}
      {face("front", inset, s - inset, s - inset, s - inset)}
      {face("rear",  inset, inset,     s - inset, inset)}
      {face("left",  inset, inset,     inset,     s - inset)}
      {face("right", s - inset, inset, s - inset, s - inset)}
    </svg>
  );
}

export default CabinetBayPreview;
